import React, { useCallback, useMemo } from 'react'
import type { QuoteDocument, OptionsDocument } from 'paintscout'
import type { EditableRate } from '@paintscout/util/builder'
import { AlertDialog } from '@paintscout/ui'
import {
  QuoteContextProvider,
  AcceptQuoteDialog,
  EditAreaDialog,
  EditAreaSubstrateDialog,
  EditGroupDialog,
  EditLineItemDialog,
  RateSelectDialog
} from '@paintscout/react-quote'
import {
  createArea,
  createRate,
  createAreaSubstrate,
  findItem,
  getEditableRateUpdateableProducts,
  moveAreaSubstrate,
  moveItems,
  removeAreaSubstrate,
  removeItems,
  updateArea,
  updateAreaSubstrate,
  updateItem
} from '@paintscout/util/builder'
import { createLineItem } from '@paintscout/util/src/builder/quote/line-items/create-line-item/create-line-item'
import { convertV1Quote } from '@paintscout/util/src/builder/quote/items/get-items/util/convert-v1-quote'
import type { AdditionalWorkOption, PainterScheduleEvent, PainterMediaItem } from '../PainterScheduleView'
import { buildPainterAdditionalWorkOptions, getPainterAdditionalWorkItemKeys } from './painterScheduleData'
import { isPainterDemoQuote } from './painterScheduleMockData'

import {
  getPainterAdditionalWorkCapabilities,
  getPainterAdditionalWorkDeleteMode,
  setPainterAdditionalWorkOriginsOnQuote
} from './painterScheduleAdditionalWorkUtils'

interface UsePainterAdditionalWorkHandlersArgs {
  eventsState: PainterScheduleEvent[]
  setEventsState: React.Dispatch<React.SetStateAction<PainterScheduleEvent[]>>
  options: OptionsDocument | Partial<OptionsDocument>
  addWorkEventId: string | null
  setAddWorkEventId: (id: string | null) => void
  setShowAdditionalWorkPanel: (show: boolean) => void
  setMediaPanel: (panel: { title: string; items: PainterMediaItem[] } | null) => void
  persistPainterQuote: (eventId: string, quote: QuoteDocument) => Promise<QuoteDocument>
  openDialog: (component: any, props?: any) => void
  dismissDialog: () => void
}

function withPainterQuoteDialogContext<P extends { quote: QuoteDocument }>(Component: React.ComponentType<P>) {
  function PainterQuoteDialog(props: P) {
    const { quote } = props

    return React.createElement(
      QuoteContextProvider,
      {
        quote,
        contextValue: {
          quote,
          cleanQuote: quote,
          isEditable: true,
          isNoteEditable: true,
          canAddAdditionalWork: true
        }
      },
      React.createElement(Component, props)
    )
  }

  PainterQuoteDialog.displayName = `PainterQuoteDialogContext(${Component.displayName ?? Component.name ?? 'Dialog'})`

  return PainterQuoteDialog
}

const PainterEditAreaDialog = withPainterQuoteDialogContext(EditAreaDialog)
const PainterEditAreaSubstrateDialog = withPainterQuoteDialogContext(EditAreaSubstrateDialog)
const PainterEditGroupDialog = withPainterQuoteDialogContext(EditGroupDialog)
const PainterEditLineItemDialog = withPainterQuoteDialogContext(EditLineItemDialog)
const PainterRateSelectDialog = withPainterQuoteDialogContext(RateSelectDialog)

function buildDemoAdditionalWorkOptions(sourceQuote: QuoteDocument): AdditionalWorkOption[] {
  return Object.values((sourceQuote.lineItems as any) ?? {})
    .filter((item: any) => item?.section === 'options' && item?.show !== false && item?.key)
    .map((item: any) => ({
      id: item.key as string,
      areaName: item.name ?? '',
      optionName: typeof item.description === 'string' ? item.description : '',
      itemType: 'lineItem' as const,
      hours: typeof item.hours === 'number' ? item.hours : undefined,
      price: typeof item.value === 'number' ? item.value : undefined,
      includesPhotos: Array.isArray(item.files) && item.files.length > 0
    }))
}

function buildAdditionalWorkStubQuote(): QuoteDocument {
  return {
    _id: `stub-${Date.now()}`,
    areas: {},
    lineItems: {},
    substrates: {},
    groups: {},
    order: { area: [], substrate: [], line_item: [] },
    files: [],
    totals: {},
    status: { value: 'draft' },
    type: 'quote',
    version: 6,
    is_invoice: false,
    int_ext: { value: 'int' },
    options: {}
  } as unknown as QuoteDocument
}

function normalizePainterEditableQuote(inputQuote?: QuoteDocument): QuoteDocument {
  const baseQuote = inputQuote ? convertV1Quote(inputQuote) : buildAdditionalWorkStubQuote()
  const files = Array.isArray((baseQuote as any).files)
    ? Object.fromEntries(
        ((baseQuote as any).files ?? []).filter((file: any) => file?.key).map((file: any) => [file.key, file])
      )
    : { ...((baseQuote as any).files ?? {}) }

  return {
    ...baseQuote,
    areas: Object.fromEntries(
      Object.entries(baseQuote.areas ?? {}).map(([key, area]: [string, any]) => [
        key,
        {
          substrate_string: {},
          substrateOrder: [],
          substrates: {},
          totals: {},
          ...area
        }
      ])
    ),
    lineItems: { ...((baseQuote as any).lineItems ?? {}) },
    substrates: { ...((baseQuote as any).substrates ?? {}) },
    groups: { ...((baseQuote as any).groups ?? {}) },
    files,
    order: {
      area: [...((baseQuote as any).order?.area ?? [])],
      substrate: [...((baseQuote as any).order?.substrate ?? [])],
      line_item: [...((baseQuote as any).order?.line_item ?? [])]
    }
  } as QuoteDocument
}

export function usePainterAdditionalWorkHandlers({
  eventsState,
  setEventsState,
  options,
  addWorkEventId,
  setAddWorkEventId,
  setShowAdditionalWorkPanel,
  setMediaPanel,
  persistPainterQuote,
  openDialog,
  dismissDialog
}: UsePainterAdditionalWorkHandlersArgs) {
  const findAreaKeyForSubstrate = useCallback((quote: QuoteDocument, substrateKey: string) => {
    return Object.keys(quote.areas ?? {}).find((areaKey) =>
      quote.areas?.[areaKey]?.substrateOrder?.some((orderItem: any) => orderItem.key === substrateKey)
    )
  }, [])

  const queuePainterDialogOpen = useCallback(
    (component: any, props: any) => {
      window.setTimeout(() => {
        openDialog(component, props)
      }, 0)
    },
    [openDialog]
  )

  const findAdditionalWorkContext = useCallback(
    (additionalWorkId: string) => {
      for (const event of eventsState) {
        const item = event.additionalWork?.find((entry) => entry.id === additionalWorkId)
        if (item) return { event, item }
      }
      return null
    },
    [eventsState]
  )

  const handleViewMedia = useCallback(
    (additionalWorkId: string) => {
      for (const event of eventsState) {
        const aw = event.additionalWork?.find((a) => a.id === additionalWorkId)
        if (aw) {
          if (!aw.media?.length) {
            openDialog(AlertDialog, {
              title: 'No Media Available',
              message: 'There is no media to view for this additional work.',
              onClose: dismissDialog,
              actions: [
                {
                  label: 'OK',
                  onClick: dismissDialog
                }
              ]
            })
            return
          }
          setMediaPanel({ title: aw.name, items: aw.media ?? [] })
          return
        }
      }
    },
    [dismissDialog, eventsState, openDialog, setMediaPanel]
  )

  const openAdditionalWorkEditDialog = useCallback(
    (eventId: string, quote: QuoteDocument, itemKey: string, section: 'pending' | 'additional') => {
      const editableQuote = normalizePainterEditableQuote(quote)
      const sourceItem = findItem({
        quote: editableQuote,
        options: options as OptionsDocument,
        key: itemKey,
        section
      }) as any

      if (!sourceItem) {
        const areaKey = findAreaKeyForSubstrate(editableQuote, itemKey)
        if (!areaKey) return

        queuePainterDialogOpen(PainterEditAreaSubstrateDialog, {
          quote: editableQuote,
          areaKey,
          substrateKey: itemKey,
          maxWidth: 'lg',
          fullWidth: true,
          nextItemKey: null,
          previousItemKey: null,
          onConfirm: async (updatedQuote: QuoteDocument) => {
            dismissDialog()
            await persistPainterQuote(eventId, updatedQuote)
          },
          onCancel: dismissDialog
        } as any)
        return
      }

      const sharedProps = {
        quote: editableQuote,
        item: sourceItem,
        section,
        consumer: 'customer' as const,
        isPainterView: true,
        onConfirm: async (updatedQuote: QuoteDocument) => {
          dismissDialog()
          await persistPainterQuote(eventId, updatedQuote)
        },
        onCancel: dismissDialog
      }

      if (sourceItem.type === 'area') {
        openDialog(PainterEditAreaDialog, sharedProps)
        return
      }
      if (sourceItem.type === 'substrate') {
        const areaKey = findAreaKeyForSubstrate(editableQuote, itemKey)
        if (!areaKey) return

        queuePainterDialogOpen(PainterEditAreaSubstrateDialog, {
          quote: editableQuote,
          areaKey,
          substrateKey: itemKey,
          maxWidth: 'lg',
          fullWidth: true,
          nextItemKey: null,
          previousItemKey: null,
          onConfirm: async (updatedQuote: QuoteDocument) => {
            dismissDialog()
            await persistPainterQuote(eventId, updatedQuote)
          },
          onCancel: dismissDialog
        } as any)
        return
      }
      if (sourceItem.type === 'group') {
        openDialog(PainterEditGroupDialog, sharedProps as any)
        return
      }
      openDialog(PainterEditLineItemDialog, sharedProps as any)
    },
    [dismissDialog, findAreaKeyForSubstrate, openDialog, options, persistPainterQuote, queuePainterDialogOpen]
  )

  const handleAcceptSign = useCallback(
    (additionalWorkId: string) => {
      const context = findAdditionalWorkContext(additionalWorkId)
      const isDemo = !context?.event.sourceQuote || isPainterDemoQuote(context.event.sourceQuote)
      const quote = context?.event.sourceQuote

      openDialog(AcceptQuoteDialog, {
        quote,
        options,
        title: 'Accept Additional Work',
        additionalAccept: true,
        allowNote: false,
        onConfirm: async (_ev: React.MouseEvent<HTMLElement>, _signature: string) => {
          if (isDemo) {
            setEventsState((prev) =>
              prev.map((evt) => ({
                ...evt,
                additionalWork: evt.additionalWork?.map((aw) =>
                  aw.id === additionalWorkId
                    ? {
                        ...aw,
                        status: 'approved',
                        approvedDate: new Date().toISOString(),
                        isCreated: aw.isCreated ?? true
                      }
                    : aw
                )
              }))
            )
          } else {
            const normalizedQuote = normalizePainterEditableQuote(quote)
            const areaKey = findAreaKeyForSubstrate(normalizedQuote, context.item.sourceKey)
            const approvedQuote = areaKey
              ? moveAreaSubstrate({
                  quote: normalizedQuote,
                  options: options as OptionsDocument,
                  areaKey,
                  key: context.item.sourceKey,
                  section: 'additional'
                })
              : moveItems({
                  quote: normalizedQuote,
                  options: options as OptionsDocument,
                  keys: [context.item.sourceKey],
                  section: 'additional',
                  previousSection: 'pending'
                })
            await persistPainterQuote(context.event.id, approvedQuote)
          }
          dismissDialog()
        },
        onCancel: dismissDialog
      })
    },
    [
      dismissDialog,
      findAdditionalWorkContext,
      findAreaKeyForSubstrate,
      openDialog,
      options,
      persistPainterQuote,
      setEventsState
    ]
  )

  const handleEditAdditionalWork = useCallback(
    (additionalWorkId: string) => {
      const context = findAdditionalWorkContext(additionalWorkId)
      const canEdit = context?.item
        ? context.item.canEdit ?? getPainterAdditionalWorkCapabilities(context.item, context.event?.sourceQuote).canEdit
        : false
      if (!canEdit) return

      if (!context?.event.sourceQuote || !context.item.sourceKey || !context.item.sourceSection) {
        // Mock path: no real quote loaded yet — build a stub quote from the PainterAdditionalWork
        // data so we can open the same EditLineItemDialog used for creation
        const mockItem = context.item
        const stubLineItem = createLineItem({ section: 'pending' })
        const itemWithValues = {
          ...stubLineItem,
          label: mockItem.name ?? '',
          description: mockItem.description ?? '',
          hours: typeof mockItem.hours === 'number' ? mockItem.hours : 0,
          price: typeof mockItem.price === 'number' ? mockItem.price : 0
        }
        const baseStub = {
          _id: `stub-${Date.now()}`,
          areas: {},
          lineItems: {},
          substrates: {},
          groups: {},
          order: { area: [], substrate: [], line_item: [] },
          files: {},
          totals: {},
          status: { value: 'draft' },
          type: 'quote',
          version: 6,
          is_invoice: false,
          int_ext: { value: 'int' },
          options: {}
        } as unknown as QuoteDocument
        const stubQuote = updateItem({ quote: baseStub, item: itemWithValues, options: options as OptionsDocument })
        const resolvedItem = findItem({
          quote: stubQuote,
          options: options as OptionsDocument,
          key: stubLineItem.key
        }) as any

        queuePainterDialogOpen(PainterEditLineItemDialog, {
          quote: stubQuote,
          item: resolvedItem ?? itemWithValues,
          consumer: 'customer' as const,
          onConfirm: (updatedQuote: QuoteDocument) => {
            dismissDialog()
            const updated = findItem({
              quote: updatedQuote,
              options: options as OptionsDocument,
              key: stubLineItem.key
            }) as any
            if (!updated) return
            setEventsState((prev) =>
              prev.map((evt) => ({
                ...evt,
                additionalWork: evt.additionalWork?.map((aw) =>
                  aw.id === additionalWorkId
                    ? {
                        ...aw,
                        name: updated.label ?? updated.name ?? aw.name,
                        description: updated.description ?? aw.description,
                        hours: updated.hours ?? aw.hours,
                        price: updated.price ?? aw.price
                      }
                    : aw
                )
              }))
            )
          },
          onCancel: dismissDialog
        })
        return
      }

      void openAdditionalWorkEditDialog(
        context.event.id,
        context.event.sourceQuote,
        context.item.sourceKey,
        context.item.sourceSection
      )
    },
    [
      dismissDialog,
      findAdditionalWorkContext,
      openAdditionalWorkEditDialog,
      options,
      queuePainterDialogOpen,
      setEventsState
    ]
  )

  const handleDeleteAdditionalWork = useCallback(
    async (additionalWorkId: string) => {
      const context = findAdditionalWorkContext(additionalWorkId)
      const canDelete = context?.item
        ? context.item.canDelete ??
          getPainterAdditionalWorkCapabilities(context.item, context.event?.sourceQuote).canDelete
        : false
      if (!canDelete) return
      if (!context?.event.sourceQuote || !context.item.sourceKey || !context.item.sourceSection) {
        setEventsState((prev) =>
          prev.map((evt) => ({
            ...evt,
            additionalWork: evt.additionalWork?.filter((aw) => aw.id !== additionalWorkId)
          }))
        )
        return
      }
      const deleteMode = getPainterAdditionalWorkDeleteMode(context.item)
      if (deleteMode === 'none') return
      const editableQuote = normalizePainterEditableQuote(context.event.sourceQuote)
      const areaKey = findAreaKeyForSubstrate(editableQuote, context.item.sourceKey)
      const nextQuote =
        areaKey != null
          ? deleteMode === 'move-to-options'
            ? moveAreaSubstrate({
                quote: editableQuote,
                options: options as OptionsDocument,
                areaKey,
                key: context.item.sourceKey,
                section: 'options'
              })
            : removeAreaSubstrate({
                quote: editableQuote,
                options: options as OptionsDocument,
                areaKey,
                key: context.item.sourceKey
              })
          : deleteMode === 'move-to-options'
            ? moveItems({
                quote: editableQuote,
                options: options as OptionsDocument,
                keys: [context.item.sourceKey],
                previousSection: context.item.sourceSection,
                section: 'options'
              })
            : removeItems({
                quote: editableQuote,
                options: options as OptionsDocument,
                keys: [context.item.sourceKey],
                section: context.item.sourceSection
              })
      const updatedQuote = setPainterAdditionalWorkOriginsOnQuote(nextQuote, (entry) => ({
        preset: entry.preset.filter((key) => key !== context.item.sourceKey),
        custom: entry.custom.filter((key) => key !== context.item.sourceKey)
      }))
      await persistPainterQuote(context.event.id, updatedQuote)
    },
    [findAdditionalWorkContext, findAreaKeyForSubstrate, options, persistPainterQuote, setEventsState]
  )

  const activeAdditionalWorkEvent = useMemo(
    () => eventsState.find((event) => event.id === addWorkEventId) ?? null,
    [addWorkEventId, eventsState]
  )

  const activeAdditionalWorkOptions = useMemo(() => {
    const sourceQuote = activeAdditionalWorkEvent?.sourceQuote
    if (!sourceQuote) return []
    if (isPainterDemoQuote(sourceQuote)) {
      return buildDemoAdditionalWorkOptions(sourceQuote)
    }
    return buildPainterAdditionalWorkOptions(sourceQuote, options as OptionsDocument)
  }, [activeAdditionalWorkEvent?.sourceQuote, options])

  const handleOpenAdditionalWorkCreateDialog = useCallback(
    (eventId: string, type?: 'area' | 'lineItem', areaName?: string) => {
      const event = eventsState.find((e) => e.id === eventId)
      const sourceQuote = event?.sourceQuote
      const isMock = !sourceQuote

      if (isMock) {
        setShowAdditionalWorkPanel(false)
        setAddWorkEventId(null)
        openDialog(AlertDialog, {
          title: 'No Invoice Found',
          message:
            'This work order does not have an associated invoice. Additional work can only be added to work orders with an invoice.',
          onClose: dismissDialog,
          actions: [{ label: 'OK', onClick: dismissDialog }]
        })
        return
      }

      const baseQuote: QuoteDocument = normalizePainterEditableQuote(sourceQuote as QuoteDocument | undefined)

      const existingPendingKeys = getPainterAdditionalWorkItemKeys(baseQuote, options as OptionsDocument, 'pending')

      // Close the panel immediately so the dialog opens cleanly on top
      setShowAdditionalWorkPanel(false)
      setAddWorkEventId(null)

      const buildOnConfirm = (_itemKey?: string) => async (updatedQuote: QuoteDocument) => {
        dismissDialog()

        const nextPendingKeys = getPainterAdditionalWorkItemKeys(updatedQuote, options as OptionsDocument, 'pending')
        const newKeys = nextPendingKeys.filter((key) => !existingPendingKeys.includes(key))
        const markedQuote = setPainterAdditionalWorkOriginsOnQuote(updatedQuote, (entry) => ({
          preset: entry.preset.filter((key) => !newKeys.includes(key)),
          custom: Array.from(new Set([...entry.custom, ...newKeys]))
        }))
        await persistPainterQuote(eventId, markedQuote)
      }

      if (type === 'area') {
        const newArea = createArea({
          quote: baseQuote,
          options: options as OptionsDocument,
          quoteType: (baseQuote as any)?.int_ext?.value
        }) as any
        newArea.section = 'pending'
        if (areaName?.trim()) {
          ;(newArea as any).area_label = {
            ...(((newArea as any).area_label ?? {}) as Record<string, unknown>),
            value: areaName.trim()
          }
          ;(newArea as any).label = areaName.trim()
        }
        const updatedQuote = updateArea({
          quote: baseQuote,
          options: options as OptionsDocument,
          area: newArea
        })

        queuePainterDialogOpen(PainterEditAreaDialog, {
          quote: updatedQuote,
          item: newArea,
          isNew: true,
          consumer: 'customer' as const,
          section: 'pending',
          onConfirm: buildOnConfirm(newArea.key),
          onCancel: dismissDialog
        })
        return
      }

      if (type === 'lineItem') {
        const newLineItem = createLineItem({ section: 'pending', calculateBy: 'hourlyRate' })
        const updatedQuote = updateItem({ quote: baseQuote, item: newLineItem, options: options as OptionsDocument })
        queuePainterDialogOpen(PainterEditLineItemDialog, {
          quote: updatedQuote,
          item: newLineItem,
          isNew: true,
          isPainterView: true,
          consumer: 'customer' as const,
          onConfirm: buildOnConfirm(newLineItem.key),
          onCancel: dismissDialog
        })
        return
      }

      const newLineItem = createLineItem({ section: 'pending', calculateBy: 'hourlyRate' })
      const updatedQuote = updateItem({ quote: baseQuote, item: newLineItem, options: options as OptionsDocument })
      queuePainterDialogOpen(PainterEditLineItemDialog, {
        quote: updatedQuote,
        item: newLineItem,
        isNew: true,
        isPainterView: true,
        consumer: 'customer' as const,
        onConfirm: buildOnConfirm(newLineItem.key),
        onCancel: dismissDialog
      })
    },
    [
      dismissDialog,
      eventsState,
      options,
      persistPainterQuote,
      queuePainterDialogOpen,
      setAddWorkEventId,
      setEventsState,
      setShowAdditionalWorkPanel
    ]
  )

  const handleAddSelected = useCallback(
    async (selectedIds: string[]) => {
      if (!activeAdditionalWorkEvent?.sourceQuote) {
        setShowAdditionalWorkPanel(false)
        setAddWorkEventId(null)
        openDialog(AlertDialog, {
          title: 'No Invoice Found',
          message:
            'This work order does not have an associated invoice. Additional work can only be added to work orders with an invoice.',
          onClose: dismissDialog,
          actions: [{ label: 'OK', onClick: dismissDialog }]
        })
        return
      }
      const editableQuote = normalizePainterEditableQuote(activeAdditionalWorkEvent.sourceQuote)

      const selectedOptions = selectedIds
        .map((id) => activeAdditionalWorkOptions.find((option) => option.id === id))
        .filter(Boolean) as AdditionalWorkOption[]
      const selectedSubstrateOptions = selectedOptions.filter(
        (option) => option.itemType === 'substrate' && option.areaId
      )
      const selectedSubstrateIds = selectedSubstrateOptions.map((option) => option.id)
      const topLevelSelectedIds = selectedIds.filter((id) => !selectedSubstrateIds.includes(id))

      const movedTopLevelQuote = topLevelSelectedIds.length
        ? moveItems({
            quote: editableQuote,
            options: options as OptionsDocument,
            keys: topLevelSelectedIds,
            previousSection: 'options',
            section: 'pending'
          })
        : editableQuote
      const movedQuote = selectedSubstrateOptions.reduce(
        (quote, option) =>
          moveAreaSubstrate({
            quote,
            options: options as OptionsDocument,
            areaKey: option.areaId as string,
            key: option.id,
            section: 'pending'
          }),
        movedTopLevelQuote
      )
      const updatedQuote = setPainterAdditionalWorkOriginsOnQuote(movedQuote, (entry) => ({
        preset: Array.from(new Set([...entry.preset, ...selectedIds])),
        custom: entry.custom.filter((key) => !selectedIds.includes(key))
      }))
      setShowAdditionalWorkPanel(false)
      setAddWorkEventId(null)
      await persistPainterQuote(activeAdditionalWorkEvent.id, updatedQuote)
    },
    [
      activeAdditionalWorkEvent,
      activeAdditionalWorkOptions,
      addWorkEventId,
      options,
      persistPainterQuote,
      setAddWorkEventId,
      setEventsState,
      setShowAdditionalWorkPanel
    ]
  )

  const handleOpenAdditionalWorkCustomRateDialog = useCallback(
    (eventId: string, areaId: string) => {
      const event = eventsState.find((e) => e.id === eventId)
      const sourceQuote = normalizePainterEditableQuote(event?.sourceQuote as QuoteDocument | undefined)

      if (!sourceQuote?.areas?.[areaId]) {
        handleOpenAdditionalWorkCreateDialog(eventId, 'area')
        return
      }

      const existingPendingKeys = getPainterAdditionalWorkItemKeys(sourceQuote, options as OptionsDocument, 'pending')

      const customRate = createRate({
        custom: true,
        quoteType: (sourceQuote as any)?.int_ext?.value,
        rateSection: 'custom'
      })
      const defaultProducts = getEditableRateUpdateableProducts({
        options: options as OptionsDocument,
        rate: customRate
      })
      const newSubstrate = createAreaSubstrate({
        quote: sourceQuote,
        options: options as OptionsDocument,
        rate: customRate,
        products: defaultProducts,
        quoteSection: 'pending'
      })
      const updatedQuote = updateAreaSubstrate({
        quote: sourceQuote,
        options: options as OptionsDocument,
        areaKey: areaId,
        areaSubstrate: newSubstrate
      })

      queuePainterDialogOpen(PainterEditAreaSubstrateDialog, {
        quote: updatedQuote,
        areaKey: areaId,
        substrateKey: newSubstrate.key,
        maxWidth: 'lg',
        fullWidth: true,
        nextItemKey: null,
        previousItemKey: null,
        onConfirm: async (nextQuote: QuoteDocument) => {
          setShowAdditionalWorkPanel(false)
          setAddWorkEventId(null)
          dismissDialog()
          const nextPendingKeys = getPainterAdditionalWorkItemKeys(nextQuote, options as OptionsDocument, 'pending')
          const newKeys = nextPendingKeys.filter((key) => !existingPendingKeys.includes(key))
          const markedQuote = setPainterAdditionalWorkOriginsOnQuote(nextQuote, (entry) => ({
            preset: entry.preset.filter((key) => !newKeys.includes(key)),
            custom: Array.from(new Set([...entry.custom, ...newKeys]))
          }))
          await persistPainterQuote(eventId, markedQuote)
        },
        onCancel: () => {
          setShowAdditionalWorkPanel(false)
          setAddWorkEventId(null)
          dismissDialog()
        }
      })
    },
    [
      dismissDialog,
      eventsState,
      handleOpenAdditionalWorkCreateDialog,
      options,
      persistPainterQuote,
      queuePainterDialogOpen,
      setAddWorkEventId,
      setShowAdditionalWorkPanel
    ]
  )

  const handleOpenRateSelectDialog = useCallback(
    (eventId: string, areaId: string, areaName?: string) => {
      const event = eventsState.find((e) => e.id === eventId)
      const sourceQuote = normalizePainterEditableQuote(event?.sourceQuote as QuoteDocument | undefined)

      if (!sourceQuote?.areas?.[areaId]) {
        handleOpenAdditionalWorkCreateDialog(eventId, 'area', areaName)
        return
      }

      const existingPendingKeys = getPainterAdditionalWorkItemKeys(sourceQuote, options as OptionsDocument, 'pending')

      setShowAdditionalWorkPanel(false)
      setAddWorkEventId(null)

      queuePainterDialogOpen(PainterRateSelectDialog, {
        quote: sourceQuote,
        quoteType: (sourceQuote as any)?.int_ext?.value,
        onConfirm: (rate: EditableRate) => {
          dismissDialog()

          const defaultProducts = getEditableRateUpdateableProducts({
            options: options as OptionsDocument,
            rate
          })
          const newSubstrate = createAreaSubstrate({
            quote: sourceQuote,
            options: options as OptionsDocument,
            rate,
            products: defaultProducts,
            quoteSection: 'pending'
          })
          const updatedQuote = updateAreaSubstrate({
            quote: sourceQuote,
            options: options as OptionsDocument,
            areaKey: areaId,
            areaSubstrate: newSubstrate
          })

          queuePainterDialogOpen(PainterEditAreaSubstrateDialog, {
            quote: updatedQuote,
            areaKey: areaId,
            substrateKey: newSubstrate.key,
            maxWidth: 'lg',
            fullWidth: true,
            nextItemKey: null,
            previousItemKey: null,
            onConfirm: async (nextQuote: QuoteDocument) => {
              dismissDialog()
              const nextPendingKeys = getPainterAdditionalWorkItemKeys(nextQuote, options as OptionsDocument, 'pending')
              const newKeys = nextPendingKeys.filter((key) => !existingPendingKeys.includes(key))
              const markedQuote = setPainterAdditionalWorkOriginsOnQuote(nextQuote, (entry) => ({
                preset: entry.preset.filter((key) => !newKeys.includes(key)),
                custom: Array.from(new Set([...entry.custom, ...newKeys]))
              }))
              await persistPainterQuote(eventId, markedQuote)
            },
            onCancel: dismissDialog
          })
        },
        onCancel: dismissDialog
      })
    },
    [
      dismissDialog,
      eventsState,
      handleOpenAdditionalWorkCreateDialog,
      options,
      persistPainterQuote,
      queuePainterDialogOpen,
      setAddWorkEventId,
      setEventsState,
      setShowAdditionalWorkPanel
    ]
  )

  return {
    handleViewMedia,
    handleAcceptSign,
    handleEditAdditionalWork,
    handleDeleteAdditionalWork,
    handleOpenAdditionalWorkCreateDialog,
    handleOpenAdditionalWorkCustomRateDialog,
    handleOpenRateSelectDialog,
    handleAddSelected,
    activeAdditionalWorkEvent,
    activeAdditionalWorkOptions
  }
}
